/**
 * AN OPEN TAB AND THE BUILD NOW ON THE SERVER.
 *
 * Staff leave PharmOS open across a shift. A deploy replaces `/assets/` and
 * the tab keeps running the previous JavaScript until somebody reloads. This
 * watches for that, once a minute and whenever the window is looked at again.
 *
 * THE RELEASE IS NOTED ONCE, BEFORE ANY LAZY CHUNK. `noteRunningShell` runs
 * at the top of `main.tsx`. A route loaded afterwards inserts its own
 * `<link>`, and re-reading the document would call that a deploy.
 *
 * Dev notes null: there is no hashed asset, the watch does not start, and no
 * request is made. A failed read is silence — a blip is not an update.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { readDeployedShell } from "@/api/client";
import { shellRelease, shellReleaseFromRefs } from "@/domain/shell-release";

/** How long Later holds the question, and how often a quiet tab looks. */
export const SHELL_CHECK_MS = 60_000;

let noted: string | null = null;

/**
 * Record the release this page was served.
 * Production calls this once, at the top of `main.tsx`, before a lazy chunk
 * can insert a `<link>`. A later call replaces the note — tests do that, and
 * a second call from the app would forget the document that was actually served.
 */
export function noteRunningShell(release: string | null): void {
  noted = release;
}

/** The release noted at startup, or null in dev and in tests that have not noted one. */
export function notedShell(): string | null {
  return noted;
}

/** The release of the document currently parsed, from its own attributes. */
export function documentShellRelease(): string | null {
  if (typeof document === "undefined") return null;
  const refs: string[] = [];
  for (const node of document.querySelectorAll("script, link")) {
    if (node instanceof HTMLScriptElement) {
      if (node.getAttribute("type")?.toLowerCase() !== "module") continue;
      const src = node.getAttribute("src");
      if (src !== null) refs.push(src);
    } else if (node instanceof HTMLLinkElement) {
      const rel = node.getAttribute("rel")?.toLowerCase().split(/\s+/) ?? [];
      if (!rel.includes("stylesheet") && !rel.includes("modulepreload")) continue;
      const href = node.getAttribute("href");
      if (href !== null) refs.push(href);
    }
  }
  return shellReleaseFromRefs(refs);
}

export interface ShellUpdate {
  /** True when the server is serving a different release from the one noted. */
  open: boolean;
  /** Close the question until the next check. */
  later: () => void;
}

/**
 * Watch the deployed shell. `open` becomes true only on a real difference.
 * Later suppresses the question until `SHELL_CHECK_MS` has passed.
 */
export function useShellUpdate(): ShellUpdate {
  const [open, setOpen] = useState(false);
  const snoozeUntil = useRef(0);

  const later = useCallback(() => {
    snoozeUntil.current = Date.now() + SHELL_CHECK_MS;
    setOpen(false);
  }, []);

  useEffect(() => {
    const current = notedShell();
    if (current === null) return;
    const ac = new AbortController();
    let pending = false;
    // A focus during a read must not be dropped. The read already in flight
    // may be the previous document; the look that arrived asks for another.
    let again = false;

    const check = () => {
      if (ac.signal.aborted) return;
      if (pending) {
        again = true;
        return;
      }
      pending = true;
      again = false;
      void readDeployedShell(ac.signal).then(
        (html) => {
          if (!ac.signal.aborted && html !== null) {
            const next = shellRelease(html);
            if (next !== null) {
              if (next === current) setOpen(false);
              else if (Date.now() >= snoozeUntil.current) setOpen(true);
            }
          }
          pending = false;
          if (!ac.signal.aborted && again) check();
        },
        () => {
          pending = false;
          if (!ac.signal.aborted && again) check();
        },
      );
    };

    check();
    const timer = window.setInterval(check, SHELL_CHECK_MS);
    const onLook = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onLook);
    window.addEventListener("focus", onLook);
    return () => {
      ac.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onLook);
      window.removeEventListener("focus", onLook);
    };
  }, []);

  return { open, later };
}
